import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const API = '/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const checkingAuth = useRef(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (checkingAuth.current) return;
    checkingAuth.current = true;

    // Get stored token
    const storedToken = localStorage.getItem('auth_token');

    try {
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers,
      });
      
      if (response.ok) {
        const userData = await response.json();
        // Preserve the token if we have one stored
        if (storedToken) {
          userData.token = storedToken;
          setToken(storedToken);
        }
        setUser(userData);
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
      checkingAuth.current = false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    let responseData;
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      // Parse response body once
      responseData = await response.json();

      if (!response.ok) {
        // Extract error message as plain string to avoid cloning issues
        const errorMessage = responseData?.detail || 'Login failed';
        throw new Error(String(errorMessage));
      }
    } catch (fetchError) {
      // Ensure we only throw plain Error with string message
      // This prevents "Request object could not be cloned" errors
      if (fetchError instanceof Error) {
        throw new Error(fetchError.message);
      }
      throw new Error('Login failed. Please try again.');
    }

    // Store token in localStorage for persistence
    if (responseData.token) {
      localStorage.setItem('auth_token', responseData.token);
      setToken(responseData.token);
    }
    setUser(responseData);
    return responseData;
  };

  const register = async (name, email, password) => {
    let responseData;
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, email, password, role: 'viewer' }),
      });

      // Parse response body once
      responseData = await response.json();

      if (!response.ok) {
        // Extract error message as plain string to avoid cloning issues
        const errorMessage = responseData?.detail || 'Registration failed';
        throw new Error(String(errorMessage));
      }
    } catch (fetchError) {
      // Ensure we only throw plain Error with string message
      // This prevents "Request object could not be cloned" errors
      if (fetchError instanceof Error) {
        throw new Error(fetchError.message);
      }
      throw new Error('Registration failed. Please try again.');
    }

    // Store token in localStorage for persistence
    if (responseData.token) {
      localStorage.setItem('auth_token', responseData.token);
      setToken(responseData.token);
    }
    setUser(responseData);
    return responseData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      throw new Error('Failed to authenticate with Google');
    }

    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear token from localStorage
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    navigate('/');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithGoogle,
      handleGoogleCallback,
      logout,
      checkAuth,
      isAdmin,
      setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
